// Seguridad de marcación: dispositivos autorizados y sesión única.
//
// Vive dentro de Ajustes de Horas Extra. La idea es que marcar asistencia sólo
// se pueda desde los equipos del local: cada navegador se identifica solo, el
// admin le pone nombre y lo aprueba, y los demás quedan bloqueados al marcar.
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
    FiShield, FiMonitor, FiSmartphone, FiCheck, FiEdit2, FiTrash2,
    FiClock, FiUser, FiAlertTriangle, FiX, FiLogOut, FiWifi,
} from 'react-icons/fi';
import {
    subscribeToDevices, approveDevice, revokeDevice, renameDevice, deleteDevice,
    getDeviceId, saveSecuritySettings, closeUserSession, subscribeToIps,
} from '../../services/deviceService';

const whenText = (stamp) => {
    try {
        const d = stamp?.toDate ? stamp.toDate() : null;
        return d ? format(d, "d MMM · h:mm a", { locale: es }) : '—';
    } catch { return '—'; }
};

// Nombres habituales, para no tener que escribirlos a mano cada vez.
const SUGERENCIAS = ['PC Recepción', 'PC Administración', 'PC Local', 'Laptop Oficina'];

// Modal para poner nombre al equipo al autorizarlo o al renombrarlo.
const DeviceNameModal = ({ device, mode, onClose, onConfirm }) => {
    const [name, setName] = useState(device?.label || '');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { setTimeout(() => inputRef.current?.select(), 40); }, []);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const confirmar = async () => {
        if (!name.trim()) return;
        setSaving(true);
        await onConfirm(name.trim());
        setSaving(false);
    };

    const esAutorizar = mode === 'approve';

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${esAutorizar ? 'bg-emerald-600' : 'bg-sky-600'}`}>
                            {esAutorizar ? <FiCheck size={15} className="text-white" /> : <FiEdit2 size={14} className="text-white" />}
                        </div>
                        <h2 className="text-[15px] font-bold text-gray-900">
                            {esAutorizar ? 'Autorizar equipo' : 'Renombrar equipo'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"><FiX size={17} /></button>
                </div>

                <div className="px-5 py-4 space-y-4">
                    {/* Qué equipo es */}
                    <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3">
                        <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 text-gray-500">
                            {device?.esMovil ? <FiSmartphone size={16} /> : <FiMonitor size={16} />}
                        </div>
                        <div className="min-w-0 text-[12px] text-gray-600">
                            <div className="font-semibold text-gray-800">{device?.so} · {device?.navegador}</div>
                            <div className="text-gray-400 truncate">
                                Último uso: {device?.lastUserName || '—'} · {whenText(device?.lastSeenAt)}
                            </div>
                        </div>
                    </div>

                    {device?.esMovil && esAutorizar && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                            <FiAlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-[11.5px] text-amber-800 leading-snug font-medium">
                                Es un <b>móvil</b>. Si lo autorizas, esa persona podrá marcar desde donde quiera.
                                Autoriza móviles sólo si de verdad lo necesitas.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                            Nombre del equipo
                        </label>
                        <input ref={inputRef} value={name} onChange={e => setName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') confirmar(); }}
                            placeholder="Ej. PC Recepción"
                            className="w-full px-3 py-2.5 text-[14px] border border-gray-200 rounded-xl outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" />
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {SUGERENCIAS.map(sug => (
                                <button key={sug} type="button" onClick={() => setName(sug)}
                                    className="px-2.5 py-1 text-[11px] font-medium text-gray-600 bg-gray-100 hover:bg-sky-100 hover:text-sky-700 rounded-full transition-colors">
                                    {sug}
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">
                            Es el nombre que verás en el historial junto a cada marcación.
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <button onClick={onClose} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
                    <button onClick={confirmar} disabled={!name.trim() || saving}
                        className={`px-5 py-2 text-[13px] font-bold text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5 ${esAutorizar ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-sky-600 hover:bg-sky-700'}`}>
                        {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiCheck size={14} />}
                        {esAutorizar ? 'Autorizar equipo' : 'Guardar nombre'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Confirmación para quitar la autorización o eliminar de la lista.
const DeviceConfirmModal = ({ device, mode, onClose, onConfirm }) => {
    const [saving, setSaving] = useState(false);
    const esQuitar = mode === 'revoke';

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                <div className="px-5 py-4 flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${esQuitar ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                        <FiAlertTriangle size={17} />
                    </div>
                    <div>
                        <h2 className="text-[15px] font-bold text-gray-900">
                            {esQuitar ? 'Quitar autorización' : 'Eliminar equipo'}
                        </h2>
                        <p className="text-[12.5px] text-gray-600 mt-1 leading-snug">
                            {esQuitar ? (
                                <>Desde <b>{device?.label}</b> ya no se podrá marcar asistencia. Seguirá en la lista por si quieres volver a autorizarlo.</>
                            ) : (
                                <>Se quita <b>{device?.label}</b> de la lista. Si vuelven a abrir GestorPro en ese equipo, aparecerá otra vez como pendiente.</>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <button onClick={onClose} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
                    <button onClick={async () => { setSaving(true); await onConfirm(); setSaving(false); }} disabled={saving}
                        className={`px-5 py-2 text-[13px] font-bold text-white rounded-lg disabled:opacity-40 ${esQuitar ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}>
                        {esQuitar ? 'Quitar autorización' : 'Eliminar'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Cierre remoto de la sesión de una persona.
const SessionCloseModal = ({ user, onClose, onConfirm }) => {
    const [saving, setSaving] = useState(false);
    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                <div className="px-5 py-4 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                        <FiLogOut size={17} />
                    </div>
                    <div>
                        <h2 className="text-[15px] font-bold text-gray-900">Cerrar sesión</h2>
                        <p className="text-[12.5px] text-gray-600 mt-1 leading-snug">
                            Se cerrará la sesión de <b>{user?.displayName || user?.email}</b> en
                            {' '}<b>{user?.activeDeviceLabel || 'su equipo'}</b>. Tendrá que volver a iniciar sesión.
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <button onClick={onClose} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
                    <button onClick={async () => { setSaving(true); await onConfirm(); setSaving(false); }} disabled={saving}
                        className="px-5 py-2 text-[13px] font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40">
                        Cerrar sesión
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const SecurityDevices = ({ security = {}, users = [] }) => {
    const [devices, setDevices] = useState([]);
    const [ips, setIps] = useState([]);
    const [saving, setSaving] = useState(false);
    const thisDeviceId = getDeviceId();

    useEffect(() => {
        const unsub = subscribeToDevices(setDevices);
        const unsubIps = subscribeToIps(setIps);
        return () => { unsub(); unsubIps(); };
    }, []);

    const toggle = async (key, value) => {
        setSaving(true);
        try {
            await saveSecuritySettings({ [key]: value });
            toast.success('Ajuste guardado');
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar');
        }
        setSaving(false);
    };

    // { device, mode: 'approve' | 'rename' | 'revoke' | 'delete' }
    const [modal, setModal] = useState(null);
    const [sessionTarget, setSessionTarget] = useState(null);
    const cerrar = () => setModal(null);

    const cerrarSesionDe = async () => {
        try {
            await closeUserSession(sessionTarget.id);
            toast.success(`Sesión de ${sessionTarget.displayName || sessionTarget.email} cerrada`);
            setSessionTarget(null);
        } catch { toast.error('No se pudo cerrar la sesión'); }
    };

    const guardarNombre = async (label) => {
        const { device, mode } = modal;
        try {
            if (mode === 'approve') { await approveDevice(device.id, label); toast.success('Equipo autorizado'); }
            else { await renameDevice(device.id, label); toast.success('Nombre actualizado'); }
            cerrar();
        } catch { toast.error('No se pudo guardar'); }
    };

    const confirmarAccion = async () => {
        const { device, mode } = modal;
        try {
            if (mode === 'revoke') { await revokeDevice(device.id); toast.success('Autorización retirada'); }
            else { await deleteDevice(device.id); toast.success('Equipo eliminado'); }
            cerrar();
        } catch { toast.error('No se pudo completar'); }
    };

    const aprobados = devices.filter(d => d.approved);
    const pendientes = devices.filter(d => !d.approved);

    // Sesiones abiertas: se leen del documento de cada usuario.
    const sesiones = users
        .filter(u => u.activeSessionId)
        .sort((a, b) => (b.activeSessionAt?.toMillis?.() || 0) - (a.activeSessionAt?.toMillis?.() || 0));

    const Row = ({ d }) => (
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${d.approved ? 'border-emerald-100 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${d.approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {d.esMovil ? <FiSmartphone size={16} /> : <FiMonitor size={16} />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold text-gray-800 truncate">{d.label || 'Equipo sin nombre'}</span>
                    {d.id === thisDeviceId && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 shrink-0">Este equipo</span>
                    )}
                    {d.esMovil && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 shrink-0">Móvil</span>
                    )}
                </div>
                <div className="text-[11px] text-gray-500 truncate flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1"><FiUser size={9} /> {d.lastUserName || '—'}</span>
                    <span className="inline-flex items-center gap-1"><FiClock size={9} /> {whenText(d.lastSeenAt)}</span>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                {d.approved ? (
                    <>
                        <button onClick={() => setModal({ device: d, mode: 'rename' })} className="p-1.5 text-gray-400 hover:text-sky-600 hover:bg-white rounded-lg" title="Renombrar"><FiEdit2 size={13} /></button>
                        <button onClick={() => setModal({ device: d, mode: 'revoke' })} className="px-2 py-1 text-[11px] font-bold text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100">Quitar</button>
                    </>
                ) : (
                    <button onClick={() => setModal({ device: d, mode: 'approve' })} className="px-2.5 py-1 text-[11px] font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-1">
                        <FiCheck size={12} /> Autorizar
                    </button>
                )}
                <button onClick={() => setModal({ device: d, mode: 'delete' })} className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-white rounded-lg" title="Eliminar"><FiTrash2 size={13} /></button>
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-5">
            <div>
                <h2 className="text-[15px] font-bold text-gray-800 mb-1 flex items-center gap-2">
                    <FiShield className="text-sky-600" size={16} /> Seguridad de marcación
                </h2>
                <p className="text-gray-500 text-sm">
                    Limita desde dónde se puede marcar asistencia. Entrar a la app sigue siendo libre:
                    así el equipo puede ver su recibo o justificar una falta desde casa.
                </p>
            </div>

            {/* Interruptores */}
            <div className="grid sm:grid-cols-2 gap-3">
                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${security.requireTrustedDevice ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" disabled={saving} checked={!!security.requireTrustedDevice}
                        onChange={e => toggle('requireTrustedDevice', e.target.checked)}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer mt-0.5" />
                    <span>
                        <span className="block text-[13px] font-bold text-gray-800">Sólo equipos autorizados</span>
                        <span className="block text-[11.5px] text-gray-500 mt-0.5">
                            Marcar asistencia queda bloqueado desde cualquier equipo que no esté en la lista de abajo.
                        </span>
                    </span>
                </label>

                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${security.singleSession ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" disabled={saving} checked={!!security.singleSession}
                        onChange={e => toggle('singleSession', e.target.checked)}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer mt-0.5" />
                    <span>
                        <span className="block text-[13px] font-bold text-gray-800">Sesión única por cuenta</span>
                        <span className="block text-[11.5px] text-gray-500 mt-0.5">
                            Al iniciar sesión en otro sitio, la sesión anterior se cierra sola.
                        </span>
                    </span>
                </label>
            </div>

            {security.requireTrustedDevice && aprobados.length === 0 && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <FiAlertTriangle size={15} className="text-red-600 shrink-0 mt-0.5" />
                    <p className="text-[12.5px] text-red-800 font-medium">
                        No hay ningún equipo autorizado todavía: <b>nadie puede marcar</b>. Autoriza al menos
                        el equipo del local antes de dejar activo este control.
                    </p>
                </div>
            )}

            {/* Pendientes */}
            {pendientes.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-[0.15em] text-amber-600 font-bold mb-2">
                        Pendientes de autorizar ({pendientes.length})
                    </h3>
                    <div className="space-y-2">
                        {pendientes.map(d => <Row key={d.id} d={d} />)}
                    </div>
                </div>
            )}

            {/* Autorizados */}
            <div>
                <h3 className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">
                    Equipos autorizados ({aprobados.length})
                </h3>
                {aprobados.length === 0 ? (
                    <p className="text-[12.5px] text-gray-400">
                        Todavía ninguno. Abre GestorPro en el equipo del local y aparecerá aquí para autorizarlo.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {aprobados.map(d => <Row key={d.id} d={d} />)}
                    </div>
                )}
            </div>

            {/* Sesiones abiertas */}
            {sesiones.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">
                        Sesiones abiertas ({sesiones.length})
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {sesiones.map(u => {
                            // Se cruza la sesión con la lista de equipos para saber si esa
                            // persona está en un equipo autorizado o no.
                            const dev = devices.find(d => d.id === u.activeDeviceId);
                            const autorizado = !!dev?.approved;
                            return (
                                <div key={u.id} className={`flex items-center gap-2.5 p-2.5 border rounded-xl ${autorizado ? 'border-emerald-100 bg-emerald-50/40' : 'border-gray-100 bg-gray-50/50'}`}>
                                    {u.photoURL
                                        ? <img src={u.photoURL} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                                        : <div className="w-7 h-7 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold flex items-center justify-center shrink-0">{(u.displayName || u.email || '?').charAt(0).toUpperCase()}</div>}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[12px] font-semibold text-gray-700 truncate">{u.displayName || u.email}</div>
                                        <div className="text-[10.5px] text-gray-400 truncate">
                                            {dev?.label || u.activeDeviceLabel || 'equipo desconocido'} · {whenText(u.activeSessionAt)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {dev && !autorizado && (
                                            <button onClick={() => setModal({ device: dev, mode: 'approve' })}
                                                className="px-2 py-1 text-[10.5px] font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                                                title="Autorizar el equipo desde el que está conectada">
                                                Autorizar equipo
                                            </button>
                                        )}
                                        {autorizado && (
                                            <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Autorizado</span>
                                        )}
                                        {!dev && (
                                            // Sesión abierta antes de que existiera el registro de
                                            // equipos: no hay nada que autorizar hasta que recargue.
                                            <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 cursor-help"
                                                title="Esta sesión se abrió antes de que el equipo quedara registrado. Pídele que recargue la página (F5) y aparecerá aquí para autorizarlo.">
                                                Sin registrar
                                            </span>
                                        )}
                                        <button onClick={() => setSessionTarget(u)}
                                            className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-white rounded-lg"
                                            title="Cerrar esta sesión">
                                            <FiLogOut size={13} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Redes desde las que se conecta el equipo */}
            <div>
                <h3 className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">
                    Redes vistas ({ips.length})
                </h3>
                {ips.length === 0 ? (
                    <p className="text-[12.5px] text-gray-400">
                        Todavía sin datos. Se irán registrando conforme el equipo inicie sesión.
                    </p>
                ) : (
                    <>
                        <div className="space-y-2">
                            {ips.map(n => (
                                <div key={n.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                                    <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 text-gray-500">
                                        <FiWifi size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-bold text-gray-800 font-mono">{n.ip}</div>
                                        <div className="text-[11px] text-gray-500 truncate">
                                            {(n.deviceLabels || []).join(' · ') || '—'}
                                        </div>
                                        <div className="text-[10.5px] text-gray-400 truncate">
                                            {(n.userNames || []).filter(Boolean).join(', ')}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[13px] font-extrabold text-gray-700 tabular-nums">{n.count || 0}</div>
                                        <div className="text-[10px] text-gray-400">conexiones</div>
                                        <div className="text-[10px] text-gray-400 mt-0.5">{whenText(n.lastSeenAt)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className={`text-[11.5px] mt-2.5 font-medium ${ips.length === 1 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {ips.length === 1
                                ? 'Hasta ahora siempre la misma IP. Si sigue así unos días, tu conexión es fija (o muy estable) y sirve para validar la red de la oficina.'
                                : `Se han visto ${ips.length} IPs distintas. Puede ser porque tienes dos conexiones, porque alguien se conecta desde fuera, o porque tu IP es dinámica y cambia sola.`}
                        </p>
                    </>
                )}
            </div>

            <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3">
                Un equipo se identifica por el navegador. Si alguien borra los datos del navegador o usa
                una ventana de incógnito, volverá a aparecer como pendiente.
            </p>

            {modal && (modal.mode === 'approve' || modal.mode === 'rename') && (
                <DeviceNameModal device={modal.device} mode={modal.mode} onClose={cerrar} onConfirm={guardarNombre} />
            )}
            {modal && (modal.mode === 'revoke' || modal.mode === 'delete') && (
                <DeviceConfirmModal device={modal.device} mode={modal.mode} onClose={cerrar} onConfirm={confirmarAccion} />
            )}
            {sessionTarget && (
                <SessionCloseModal user={sessionTarget} onClose={() => setSessionTarget(null)} onConfirm={cerrarSesionDe} />
            )}
        </div>
    );
};

export default SecurityDevices;
